import {createSelector} from 'reselect';

import {getAbacusTests, getCarousel, getTranslations} from './store';
import {getListingCarousel, getVirtualTourData} from './listing';
import getListingPageModalStates from './modalStates';
import {getBaseUrlPath, getHeight, getIsMobile, getSystemClientSideRendered} from './system';
import {evaluateExperiment, isInBucketData} from '../utils/abacusHelper';
import getIndexSibling from '../../server/utils/carouselUtils';
import {labelMaker} from '../../server/utils/labelMaker';

const getMainImageHeight = createSelector(
    [getAbacusTests, getHeight, getIsMobile],
    (abacusTests, windowHeight, isMobile) => {
        if (isMobile) {
            return 270;
        }

        if (
            !evaluateExperiment({
                abacus: abacusTests,
                name: 'ha_pdp_do_not_mount_carousel_on_page_load',
                variant: 1,
                deferLogging: true
            })
        ) {
            return 512;
        }

        const safeHeight = windowHeight ? windowHeight - 300 : 512;
        return Math.max(Math.min(safeHeight, 800), 512);
    }
);

/* eslint-disable complexity */
const getImages = createSelector(
    [getListingCarousel, getVirtualTourData, getAbacusTests, getIsMobile, getTranslations],
    (listingCarousel, virtualTourData, abacusTests, isMobile, translations) => {
        let {images} = listingCarousel;
        const {virtualTourUrl, virtualTourHeroImage} = virtualTourData;
        let shouldVRImageIncluded = false;
        // Determine if VRTour should be included.
        if (virtualTourUrl) {
            if (isMobile) {
                if (isInBucketData(abacusTests, 'ha_virtual_tour_viewer_pdp_mobile', 1)) {
                    shouldVRImageIncluded = true;
                }
            } else if (isInBucketData(abacusTests, 'ha_pdp_virtual_tour_viewer', 1)) {
                shouldVRImageIncluded = true;
            }
        }
        const useHeroImage = virtualTourHeroImage;
        const heroImgSrc = useHeroImage
            ? virtualTourHeroImage
            : 'https://odis.homeaway.com/odis/listing/202cb713-2fd0-4959-8003-9a70b6941c77.c10.jpg';
        if (shouldVRImageIncluded) {
            const virtualTourObject = {
                mediaType: 'virtualtour',
                lowResSrc: heroImgSrc,
                src: heroImgSrc,
                altText: useHeroImage
                    ? translations.getVirtualWalkthrough
                    : translations.takeVirtualTour,
                width: 1024,
                height: 768,
                type: useHeroImage ? 'VT' : 'image',
                slideHeightStyle: '60vh',
                labels: {
                    fullscreen: translations.tourThisProperty,
                    button: translations.takeVirtualTour,
                    title: translations.virtualWalkthrough
                }
            };

            images =
                images &&
                images
                    .slice(0, 2)
                    .concat(virtualTourObject)
                    .concat(images.slice(2));
        }
        return images || [];
    }
);
/* eslint-enable complexity */

const getImageCarouselData = createSelector(
    [
        getSystemClientSideRendered,
        getCarousel,
        getListingCarousel,
        getTranslations,
        getAbacusTests,
        getVirtualTourData,
        getImages,
        getMainImageHeight,
        getListingPageModalStates,
        getIsMobile,
        getBaseUrlPath
    ],
    (
        clientSideRendered,
        carousel,
        listingCarousel,
        translations,
        abacusTests,
        virtualTourData,
        images,
        mainImageHeight,
        listingPageModalStates,
        isMobile,
        baseUrlPath
    ) => {
        const prevIndex = getIndexSibling(false, carousel.index, images);
        const nextIndex = getIndexSibling(true, carousel.index, images);
        const prevPrevIndex = getIndexSibling(false, prevIndex, images);
        const nextNextIndex = getIndexSibling(true, nextIndex, images);
        const renderedIndices = [
            prevPrevIndex,
            prevIndex,
            carousel.index,
            nextIndex,
            nextNextIndex
        ];

        const {virtualTourId, virtualTourUrl} = virtualTourData;

        return {
            galleryTitle: labelMaker(translations.photoGallery, images.length),
            fullScreen: listingPageModalStates.imageCarouselModalOpen,
            isVerticalGalleryOpen: listingPageModalStates.verticalGalleryModalOpen,
            renderedLowResCountPerSide: listingCarousel.renderedLowResCountPerSide,
            renderInteractive: clientSideRendered,
            thumbnailUrl: listingCarousel.thumbnailUrl,
            images,
            index: carousel.index,
            prevIndex,
            renderedIndices,
            hasImages: !!images.length,
            virtualTourId,
            virtualTourUrl,
            labels: translations,
            mainImageHeight,
            mainImageClickDisabled:
                listingPageModalStates.datePickerModalOpen ||
                listingPageModalStates.guestSelectorModalOpen,
            isMobile,
            baseUrlPath
        };
    }
);

export default getImageCarouselData;
export {getMainImageHeight, getImages, getImageCarouselData};
